<template>
  <InputSelect
    v-model="model"
    data-testid="contact-list-dropdown"
    :disabled="disabled"
    :dropdown-text="
      model?.name || getLocaleText('outreach_contact_lists_dropdown.select_one')
    "
    :label="getLocaleText('outreach_contact_lists_dropdown.label')"
    :loading="loading"
    new-design
    option-label="name"
    option-prop="id"
    :options="filteredListOptions"
    @click="loadLists"
  >
    <template #beforeOptions>
      <InputText
        v-model="filter"
        :auto-focus="isDesktop"
        class="input"
        clearable
        data-testid="contact-list-search-input"
        :placeholder="
          getLocaleText('outreach_contact_lists_dropdown.search_placeholder')
        "
        tabindex="-1"
        @click.stop
      />
    </template>

    <template
      v-if="!filteredListOptions.length"
      #empty
    >
      <div data-testid="contact-list-no-results">
        {{ getLocaleText("outreach_contact_lists_dropdown.no_results") }}
      </div>
    </template>
  </InputSelect>
</template>

<script setup>
import { useQuasar } from "quasar";
import { computed, inject, ref } from "vue";

import { getLocaleText } from "shared/boot/i18n";
import { InputSelect, InputText } from "shared/components/base";
import useTeamPicker from "shared/composables/useTeamPicker";
import { ContactList } from "shared/resources";
import features from "shared/services/features";

const isDesktop = inject("isDesktop");

const model = defineModel({
  type: Object,
});

defineProps({
  disabled: {
    type: Boolean,
  },
});

const $q = useQuasar();

const loading = ref(false);
const options = ref([]);
const filter = ref("");

const filteredListOptions = computed(() =>
  options.value.filter(({ name }) =>
    name.toLowerCase().includes(filter.value.toLowerCase())
  )
);

async function loadLists() {
  if (options.value.length) return;

  loading.value = true;

  try {
    const { selectedTeamPreference } = useTeamPicker();

    const { data } = await ContactList.where({
      contactListsContactsCount: { gt: 0 },
      ...(features.has("has_team_workspaces")
        ? { organisationTeamId: [selectedTeamPreference.value, "null"] }
        : {}),
    })
      .order({ updatedAt: "desc" })
      .per(999)
      .all();

    options.value = data;
  } catch {
    $q.notify(getLocaleText("outreach_contact_lists_dropdown.loading_error"));
  } finally {
    loading.value = false;
  }
}
</script>

<style lang="scss" scoped>
.list-option {
  &__empty {
    padding: 8px 12px;
    cursor: pointer;
    font-size: 13px;
    line-height: 18px;
  }
}
</style>
